/* eslint-disable react/no-danger */
import React, { useContext } from "react";
import Image from "components/image";
import Link from "helpers/link";
import { PortableText } from "@portabletext/react";
import getButtonStyle from "helpers/get-button-style";
import hasContainer from "helpers/has-container";
import getModulePadding from "helpers/get-module-padding";
import clean from "helpers/sanitize-html";
import { DynamicContentContext } from "@root/context/dynamic-content";
import richTextData from "./data";
import TablesBlock from "components/tables-block";
import AccordionBlock from "components/accordion-block";
import GridBlock from "components/grid-block";
import IndexedListBlock from "components/indexed-list-block";
import HorizontalDivider from "components/horizontal-divider";
import ButtonsList from "components/buttons-list";
import InfluencersVideoGrid from "components/influencers-video-grid";
import ImageBlock from "components/image-block";
import getColorSanity from "@root/helpers/get-color-sanity";
import convertToEmbed from "helpers/youtube-embedded";

// Two different stylings for a full module richtext
const templateClass = {
    Homepage: "homepage-richtext", Blog: "blog-richtext-new", NoStyle: "nostyle-richtext",
};

const Video = ({ value }) => {
    const { controls, autoplay, muted, loop, video, poster } = value;
    return (
        <video className="rich-text-new__video" controls={controls} autoPlay={autoplay} muted={muted} loop={loop} poster={poster || undefined}>
            <track kind="captions" />
            <source src={video} type="video/mp4" />
            Your browser does not support HTML5 video.
        </video>
    );
};

const Code = (value) => {
    const { text } = value;
    const sanitizedHtml = clean(text);

    return <code dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

// Types
const ImageRenderer = ({ value: node }) => <Image className="rich-text-new__image rich-text-new__image--centered" image={node} alt={node.alt} to={node.url} />;

const ImageWithWidthRenderer = ({ value: node }) => (
    <div style={{ maxWidth: node.width }} className="rich-text-new__image-container rich-text-new__image-container--centered">
        <Image image={node} alt={node.alt} to={node.url} />
    </div>
);

const ImageWithHeightRenderer = ({ value: node }) => <Image className="rich-text-new__image" image={node} alt={node.alt} to={node.url} style={{ maxHeight: node.height }} />;

const VideoEmbed = ({ value: node }) => {
    const alignMapping = {
        left: "rich-text-new__video-embed--align-left",
        center: "rich-text-new__video-embed--align-center",
        right: "rich-text-new__video-embed--align-right",
    };

    const ratioMapping = {
        default: "rich-text-new__video-embed-wrapper--ratio-default",
        wide: "rich-text-new__video-embed-wrapper--ratio-wide",
        square: "rich-text-new__video-embed-wrapper--ratio-square",
    };

    return (
        <div className={`rich-text-new__video-embed ${alignMapping[node.videoAlign] || ""}`}>
            <div className="rich-text-new__video-embed-container" style={{ maxWidth: node.maxWidth }}>
                <div className={`rich-text-new__video-embed-wrapper ${ratioMapping[node.ratio] || ratioMapping.default}`}>
                    <iframe className="rich-text-new__video-embed-iframe" referrerPolicy="strict-origin-when-cross-origin" src={node.url} title={node.title} />
                </div>
            </div>
        </div>
    );
};

const YoutubeEmbedInline = ({ value: node }) => {
    if (!node?.url) return null;
    
    let embedUrl = node.url;
    
    // If URL is already an embed URL, use it as is
    if (embedUrl.includes('youtube.com/embed/')) {
        // Already in embed format
    } else if (embedUrl.includes('youtu.be/')) {
        // Handle youtu.be short URLs
        const videoId = embedUrl.split('youtu.be/')[1]?.split('?')[0]?.split('&')[0];
        if (videoId) {
            embedUrl = `https://www.youtube.com/embed/${videoId}`;
        }
    } else {
        // Convert standard YouTube URL to embed format
        embedUrl = convertToEmbed(node.url);
    }
    
    return (
        <div className="rich-text-new__youtube-embed-inline">
            <iframe
                className="rich-text-new__youtube-embed-inline-iframe"
                src={embedUrl}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
            />
        </div>
    );
};

const ButtonRenderer = ({ value: node }) => (
    <Link className={getButtonStyle(node?.style)} to={node?.url}>
        {node?.text}
    </Link>
);

// Marks
const Links = ({ children, value }) => <Link to={value.href}>{children}</Link>;

const TextSettings = ({ children, value }) => (
    <span
        className={`${value?.extraClasses ? value?.extraClasses : ""}`}
        id={`${value?.id ? value?.id : ""}`}
        style={{
            display: "block",
            color: value?.color?.hex,
            textAlign: value?.alignment,
        }}
    >
        {children}
    </span>
);

const NestedBlock = ({ value }, template) => {
    const types = {
        types: {
            imageWithoutWidth: ImageRenderer,
            ImageWithWidth: ImageWithWidthRenderer,
            ImageWithHeight: ImageWithHeightRenderer,
            imageBlock: ({ value }) => <ImageBlock data={value} />,
            video: Video,
            videoEmbed: VideoEmbed,
            youtubeEmbedInline: YoutubeEmbedInline,
            ...template?.types,
        },
        marks: {
            link: Links,
            code: Code,
            ...template?.marks,
        },
        block: { ...template?.block },
        list: { ...template?.list },
        listItem: { ...template?.listItem },
    };

    const computeWidthRatio = (ratio, isLeft) => {
        if (!ratio || typeof ratio !== "string") {
            return isLeft ? "rich-text-new__nested-block--left-default" : "rich-text-new__nested-block--right-default";
        }

        switch (ratio) {
            case "right70":
                return isLeft ? "rich-text-new__nested-block--left-right70" : "rich-text-new__nested-block--right-right70";
            case "default":
                return isLeft ? "rich-text-new__nested-block--left-default" : "rich-text-new__nested-block--right-default";
            case "equal":
                return "rich-text-new__nested-block--equal";
            case "counterDefault":
                return isLeft ? "rich-text-new__nested-block--left-counter-default" : "rich-text-new__nested-block--right-counter-default";
            case "left70":
                return isLeft ? "rich-text-new__nested-block--left-left70" : "rich-text-new__nested-block--right-left70";
            default:
                return isLeft ? "rich-text-new__nested-block--left-default" : "rich-text-new__nested-block--right-default";
        }
    };

    return (
        <div className={`rich-text-new__nested-block ${value.mobileFlexReverse ? "rich-text-new__nested-block--reverse-mobile" : ""}`}>
            {value.leftBlock?.length > 0 && (
                <div className={`rich-text-new__nested-block-column ${computeWidthRatio(value.blocksWidthRatio, true)}`}>
                    <PortableText
                        value={value.leftBlock}
                        components={types}
                    />
                </div>
            )}
            {value.rightBlock?.length > 0 && (
                <div className={`rich-text-new__nested-block-column ${computeWidthRatio(value.blocksWidthRatio, false)}`}>
                    <PortableText
                        value={value.rightBlock}
                        components={types}
                    />
                </div>
            )}
        </div>
    );
};

export const richTextComponents = (template) => ({
    types: {
        Image: ImageRenderer,
        ImageWithWidth: ImageWithWidthRenderer,
        ImageWithHeight: ImageWithHeightRenderer,
        videoEmbed: VideoEmbed,
        youtubeEmbedInline: YoutubeEmbedInline,
        Button: ButtonRenderer,
        Video,
        tablesBlock: ({ value }) => <TablesBlock data={value} />,
        imageBlock: ({ value }) => <ImageBlock data={value} />,
        accordion: ({ value }) => <AccordionBlock data={value} />,
        gridBlock: ({ value }) => <GridBlock data={value} />,
        indexedListBlock: ({ value }) => <IndexedListBlock data={value} />,
        horizontalDivider: ({ value }) => <HorizontalDivider data={value} />,
        buttonsList: ({ value }) => <ButtonsList data={value} />,
        influencersVideoGrid: ({ value }) => <InfluencersVideoGrid data={value} />,
        ...template?.types,
        nestedBlock: (props) => NestedBlock(props, template),
    },
    marks: {
        link: Links,
        textSettings: TextSettings,
        code: Code,
        ...template?.marks,
    },
    block: { ...template?.block },
    list: { ...template?.list },
    listItem: { ...template?.listItem },
});

const RichTextNew = ({ value, data, ...other }) => {
    const { getDynamicContent } = useContext(DynamicContentContext) || {};

    const dynamicContent = getDynamicContent?.(data.dynamicContentPath);
    const backgroundColor = data?.backgroundColor ? getColorSanity(data?.backgroundColor) : "transparent";
    const textColor = data?.textColor ? getColorSanity(data?.textColor) : null;
    const style = data?.style || "default";

    const styleClasses = {
        roundedLarge: "rich-text-new--rounded-large",
        roundedTopLarge: "rich-text-new--rounded-top-large",
        roundedBottomLarge: "rich-text-new--rounded-bottom-large",
        default: "rich-text-new--default",
    };

    const styleClass = styleClasses[style] || styleClasses.default;

    return (
        <div className={`
            full-module-richtext
            rich-text-new
            ${hasContainer(data?.moduleSettings?.hasContainer) !== "container" ? "rich-text-new--no-padding" : "container"}
            ${templateClass[data?.template]}
            ${styleClass}
            ${textColor ? "rich-text-new--has-text-color" : ""}
        `}
            style={{
                "--rich-text-new-background-color": backgroundColor,
                ...(textColor && { "--rich-text-new-text-color": textColor })
            }}
        >
            <div className={`rich-text-new__content ${data?.hasInternalPadding ? getModulePadding() : ""} richText-description`}>
                {data?.hasBorderTop
                    && <div className="rich-text-new__border-top" />}

                {dynamicContent && typeof dynamicContent === "string" && (
                    <div className="rich-text-new__dynamic-content" dangerouslySetInnerHTML={{ __html: dynamicContent }} />
                )}

                {!dynamicContent && (
                    <PortableText
                        value={value ?? data?.data}
                        components={richTextComponents(richTextData[data?.template])}
                        {...other}
                    />
                )}
            </div>
        </div>
    );
};

export default RichTextNew;
